/**
 * Thin client for the seller booking API (`/v1/seller/booking/*`). Every
 * booking subcomponent talks to the backend through this factory so the
 * endpoint shapes live in exactly one place — matching the "one conflict path"
 * philosophy in plans/completed/PLAN-BOOKING-SCHEDULING.md.
 *
 * `createBookingApi` is framework-agnostic (takes a fetch + base URL) so it can
 * be unit-tested or reused outside React; `useBookingApi` binds it to AuthContext.
 */
import { useMemo } from 'react';
import { useAuth } from '../../../helpers/AuthContext';

async function parse(response) {
    let json = null;
    try {
        json = await response.json();
    } catch (e) {
        json = null;
    }
    if (!response.ok) {
        const message = (json && (json.message || json.error)) || `Request failed (${response.status})`;
        const error = new Error(message);
        error.status = response.status;
        throw error;
    }
    return json;
}

export function createBookingApi(authFetch, apiUrl = '') {
    const base = `${apiUrl}/v1/seller/booking`;
    const req = (path, options = {}) => authFetch(`${base}${path}`, options).then(parse);
    const jsonBody = (method, body) => ({
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
    });

    return {
        // Resources
        listResources: () => req('/resources').then(r => r.data || []),
        getResource: (id) => req(`/resources/${id}`).then(r => r.data),
        createResource: (payload) => req('/resources', jsonBody('POST', payload)).then(r => r.data),
        updateResource: (id, payload) => req(`/resources/${id}`, jsonBody('PATCH', payload)).then(r => r.data),
        deleteResource: (id) => req(`/resources/${id}`, { method: 'DELETE' }),
        ensureDefaultResource: () => req('/resources/ensure-default', jsonBody('POST', {})).then(r => r.data),

        // Availability (recurring pattern lives on the resource)
        saveAvailability: (id, payload) =>
            req(`/resources/${id}/availability`, jsonBody('PUT', payload)).then(r => r.data),

        // Exceptions (blackouts / openings / overrides)
        listExceptions: (id, range) => {
            const qs = range && range.from && range.to
                ? `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`
                : '';
            return req(`/resources/${id}/exceptions${qs}`).then(r => r.data || []);
        },
        createException: (id, payload) =>
            req(`/resources/${id}/exceptions`, jsonBody('POST', payload)).then(r => r.data),
        deleteException: (id, exceptionId) =>
            req(`/resources/${id}/exceptions/${exceptionId}`, { method: 'DELETE' }),

        // Availability diagnostics (§18): which bookable products draw on this
        // calendar, and a preview of the next bookable slots (or the reason none).
        listBookableProducts: (id) =>
            req(`/resources/${id}/bookable-products`).then(r => r.data || []),
        previewSlots: (id, { productId, count } = {}) => {
            const params = new URLSearchParams({ productId });
            if (count) params.set('count', count);
            return req(`/resources/${id}/preview-slots?${params.toString()}`)
                .then(r => ({ slots: r.data?.slots || [], reason: r.meta?.reason || null }));
        },

        // Bookings agenda + actions
        listBookings: (filters = {}) => {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') params.set(key, value);
            });
            const qs = params.toString();
            return req(`/bookings${qs ? `?${qs}` : ''}`);
        },
        getBooking: (id) => req(`/bookings/${id}`).then(r => r.data),
        approveBooking: (id) => req(`/bookings/${id}/approve`, jsonBody('POST', {})).then(r => r.data),
        declineBooking: (id, reason) =>
            req(`/bookings/${id}/decline`, jsonBody('POST', { reason })).then(r => r.data),
        cancelBooking: (id, reason) =>
            req(`/bookings/${id}/cancel`, jsonBody('POST', { reason })).then(r => r.data),
    };
}

export function useBookingApi() {
    const { fetch: authFetch, API_URL } = useAuth();
    return useMemo(() => createBookingApi(authFetch, API_URL || ''), [authFetch, API_URL]);
}

export default useBookingApi;
